import { addDays, dayKey } from './date';
import type { CheckIn, Goal } from './types';

/** Index check-ins by goalId for fast lookup. */
export type CheckInIndex = Map<string, Set<string>>;

export function indexCheckIns(checkIns: CheckIn[]): CheckInIndex {
  const idx: CheckInIndex = new Map();
  for (const c of checkIns) {
    let set = idx.get(c.goal_id);
    if (!set) {
      set = new Set();
      idx.set(c.goal_id, set);
    }
    set.add(c.day);
  }
  return idx;
}

export function hasCheckIn(idx: CheckInIndex, goalId: string, day: string): boolean {
  return idx.get(goalId)?.has(day) ?? false;
}

export function doneToday(idx: CheckInIndex, goal: Goal, today: string): boolean {
  if (goal.type === 'todo') return goal.completed_at != null;
  return hasCheckIn(idx, goal.id, today);
}

/** Consecutive days ending at `today` (or yesterday, if not done today).
 *  A broken streak = 0. If done today, count includes today. If not done today
 *  but done yesterday, count starts at yesterday and walks back.
 *  Grace window: if neither today nor yesterday has a check-in, streak = 0. */
export function currentStreak(idx: CheckInIndex, goalId: string, today: Date): number {
  const todayStr = dayKey(today);
  const yesterdayStr = dayKey(addDays(today, -1));

  const set = idx.get(goalId);
  if (!set || set.size === 0) return 0;

  let cursor: Date;
  if (set.has(todayStr)) cursor = today;
  else if (set.has(yesterdayStr)) cursor = addDays(today, -1);
  else return 0;

  let count = 0;
  while (set.has(dayKey(cursor))) {
    count++;
    cursor = addDays(cursor, -1);
  }
  return count;
}

/** Longest consecutive run found in history. */
export function longestStreak(idx: CheckInIndex, goalId: string): number {
  const set = idx.get(goalId);
  if (!set || set.size === 0) return 0;
  const sorted = [...set].sort();
  let best = 1;
  let run = 1;
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1]);
    const cur = new Date(sorted[i]);
    const diff = Math.round((cur.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 1) {
      run++;
      if (run > best) best = run;
    } else {
      run = 1;
    }
  }
  return best;
}

/** Check-ins this week (Sun-based) for a goal. */
export function weekCount(idx: CheckInIndex, goalId: string, today: Date): number {
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - start.getDay());
  let n = 0;
  for (let i = 0; i < 7; i++) {
    if (hasCheckIn(idx, goalId, dayKey(addDays(start, i)))) n++;
  }
  return n;
}

/** Hit rate over last N days (0–1). */
export function hitRate(idx: CheckInIndex, goalId: string, days: number, today: Date): number {
  let hit = 0;
  for (let i = 0; i < days; i++) {
    if (hasCheckIn(idx, goalId, dayKey(addDays(today, -i)))) hit++;
  }
  return hit / days;
}

/** Map a streak count to a consistency tier (0 = no dot, 1–4 = deepening colour:
 *  fresh fern → gold → pumpkin → deep ember as the streak runs longer). */
export function streakTier(streak: number): 0 | 1 | 2 | 3 | 4 {
  if (streak <= 0) return 0;
  if (streak < 7) return 1;
  if (streak < 30) return 2;
  if (streak < 100) return 3;
  return 4;
}
